<template>
  <MinimalLayout :loading="step === 'loading' || step === 'selectingAccount'">
    <ExpiredSentStatus v-if="step === 'tokenExpired'" :message="t('link.expired')" />

    <BankAccountList
      v-else-if="step === 'selectAccount'"
      :title="t('link.selectAccount')"
      :select-button-text="t('link.select')"
      :bank-accounts="bankAccountItems"
      @select="(bankAccountId) => handleBankAccountSelected(bankAccountId, accountId)"
    />

    <SuccessStatus
      v-else-if="step === 'success'"
      :message="t('link.accountSynchronized')"
      :subtitle="t('link.closePage')"
    />

    <ErrorStatus v-else>{{ t("link.errorOccurred") }}</ErrorStatus>
  </MinimalLayout>
</template>

<script lang="ts" setup>
import ErrorStatus from "@/components/ErrorStatus.vue";
import ExpiredSentStatus from "@/components/ExpiredSentStatus.vue";
import MinimalLayout from "@/components/MinimalLayout.vue";
import SuccessStatus from "@/components/SuccessStatus.vue";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { call, db } from "@/firebase";
import { useUserStore } from "@/store";
import { tokenAuth } from "@/views/Link/tokenAuth";
import type { Account } from "@aircap/common/account";
import type { BridgeAccount } from "@aircap/common/bridge";
import { logsFullPath, type Log } from "@aircap/common/log";
import { captureException } from "@sentry/vue";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useFirebaseAuth } from "vuefire";
import BankAccountList from "./components/BankAccountList.vue";

const { t } = useI18n();
const auth = useFirebaseAuth();
const route = useRoute();
const user = useUserStore();

const accountId = ref<string | null>(null);
const step = ref<
  "loading" | "error" | "tokenExpired" | "selectAccount" | "selectingAccount" | "success"
>("loading");

const bankAccountsSource = computed(() =>
  accountId.value
    ? collection<BridgeAccount>(db, "accounts", accountId.value, "bridgeAccounts")
    : null,
);
const { data: bankAccounts } = useCollection<BridgeAccount>(bankAccountsSource);
const bankAccountItems = computed(() =>
  bankAccounts.value.map((bankAccount) => ({
    id: bankAccount.id,
    name: bankAccount.details.name,
    iban: bankAccount.details.iban,
  })),
);

watch(bankAccounts, (bankAccounts) => {
  if (step.value !== "selectAccount") return;
  if (!bankAccounts || bankAccounts.length !== 1 || !bankAccounts[0]) return;
  console.log("Only one Bridge account available, automatically selecting it");
  void handleBankAccountSelected(bankAccounts[0].id, accountId.value);
});

const handleBankAccountSelected = async (bankAccountId: string, accountId: string | null) => {
  if (step.value === "selectingAccount" || step.value === "success") return;
  console.log(`Account selected: ${bankAccountId}`);
  try {
    if (!accountId) throw new Error("projectId is null");
    console.log("Setting Bridge account...");
    step.value = "selectingAccount";
    await call("setBridgeAccount")({ bankAccountId, accountId });
    console.log("Bridge account set successfully");
    step.value = "success";
  } catch (cause) {
    const error = new Error("Error in Bridge Return handleAccountSelected function", { cause });
    console.error(error);
    captureException(error);
    step.value = "error";
  }
};

void (async () => {
  try {
    if (!auth) throw new Error("Auth not initialized");

    // Check errors from query, https://docs.bridgeapi.io/v2021.06.01/docs/out-of-the-bridge-connect
    if (route.query.success !== "true") {
      console.error(
        `Got error ${typeof route.query.success === "string" ? route.query.success : JSON.stringify(route.query.success)} from query in Bridge Return`,
        {
          query: route.query,
        },
      );
      captureException(
        new Error(
          `Got error ${typeof route.query.success === "string" ? route.query.success : JSON.stringify(route.query.success)} from query in Bridge Return`,
        ),
        {
          extra: { query: route.query },
        },
      );
      step.value = "error";
      return;
    }

    if (route.query.token && typeof route.query.token === "string") {
      const result = await tokenAuth(auth, route.query.token);
      if (!result.success) {
        if (result.tokenExpired) {
          step.value = "tokenExpired";
          return;
        }
        throw result.error;
      }
    }

    await user.waitForLoading();

    if (!user.authenticatedUser) throw new Error("User not authenticated");

    if (user.authenticatedUser.authClaims.kind !== "operator")
      throw new Error("User is not operator");

    // Set accountId to local variable to prevent reactivity issues
    const _accountId = user.authenticatedUser.authClaims.accountId;

    const accountSnap = await getDoc(doc<Account>(db, "accounts", _accountId));
    const account = accountSnap.data();
    if (!account) throw new Error(`Project ${_accountId} not found`);
    console.log(`Project ${_accountId} loaded, type is ${account.accountType}`);

    if (account.accountType !== "bridge") throw new Error(`Project account type is not bridge`);

    try {
      await addDoc(collection<Log>(db, logsFullPath(_accountId)), {
        name: "openBanking.returned",
        createdAt: Timestamp.now(),
      });
    } catch (cause) {
      const error = new Error("Error adding log entry", { cause });
      console.error(error);
      captureException(error);
    }

    if (account.bankAccountId) {
      console.log(
        `Project already has an account selected, directly selecting it: ${account.bankAccountId}`,
      );
      void handleBankAccountSelected(account.bankAccountId, _accountId);
      return;
    }

    console.log(`Showing select account step`);
    step.value = "selectAccount";

    console.log(`Setting projectId: ${_accountId}`);
    accountId.value = _accountId;
  } catch (cause) {
    const error = new Error("Error in Link BridgeReturn init function", { cause });
    console.error(error);
    captureException(error);
    step.value = "error";
  }
})();
</script>
